import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import { PolicyAction, PolicyFormValues } from '../policy-dialog';
import { SharedDriveModel } from '../../../../types';
import SharedDrivesAutocomplete from '../../../shared/shared-drives-autocomplete';
import { useUserSettings } from '../../../utils/use-user-settings';
import SharedDrivesBigQueryAutocomplete from '../../../shared/shared-drive-bigquery-autocomplete';
import z from 'zod';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface DriveInfo {
  name: string;
  id: string;
  role: string;
}

interface AddShareDriveActionProps {
  setValue: UseFormSetValue<PolicyFormValues>;
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  trigger: UseFormTrigger<PolicyFormValues>;
  errors: FieldErrors<PolicyFormValues>;
}

export const AddSharedDriveActionSchema = z
  .object({
    type: z.literal('Add_To_Shared_Drive'),
    driveRole: z.array(z.string()),
    driveName: z.array(z.string()).min(1, 'Shared Drive is required.'),
    driveId: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    // This is not visible validation error
    if (
      data.driveName.length !== data.driveId.length ||
      data.driveName.length !== data.driveRole.length
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Internal state mismatch: drive properties have different lengths.',
      });
    }
  });
export type AddShareDriveActionType = z.infer<
  typeof AddSharedDriveActionSchema
>;

// Type guard to check if an action is AddSendAsActionType
function isAddShareDriveAction(
  action: PolicyAction
): action is AddShareDriveActionType {
  return action.type === 'Add_To_Shared_Drive';
}

export default function AddShareDriveAction({
  setValue,
  control,
  watch,
  trigger,
  errors,
}: AddShareDriveActionProps) {
  const [alreadySelectedError, setAlreadySelectedError] = useState(false);
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );
  const autocompleteKey = useRef(new Date().getTime().toString());
  const { currentTier } = useUserSettings();
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Add_To_Shared_Drive');
  const actionAtIndex = actionData[idx];
  const [defaultAllShareDrivesRole, setDefaultAllShareDrivesRole] = useState(
    () =>
      isAddShareDriveAction(actionAtIndex) &&
      actionAtIndex.driveRole &&
      actionAtIndex.driveRole.length > 0
        ? actionAtIndex.driveRole[0]
        : 'reader'
  );

  if (!isAddShareDriveAction(actionAtIndex)) {
    return null;
  }

  const currentAction: AddShareDriveActionType = actionAtIndex;
  const error: FieldErrors<AddShareDriveActionType> | undefined =
    errors?.actionData?.[idx];

  // Derived constant that combines drive information
  const selectedSharedDrives: DriveInfo[] =
    currentAction.driveName
      ?.filter((name) => name !== '*')
      .map((name, index) => ({
        name,
        id: currentAction.driveId[index],
        role: currentAction.driveRole[index],
      })) || [];

  const addSelection = () => {
    if (!selectedDrive) return;

    const newlyAddedDriveIdx = currentAction.driveName
      ? currentAction.driveName.length
      : 0;
    setValue(
      `actionData.${idx}.driveName`,
      currentAction.driveName
        ? [...currentAction.driveName, selectedDrive.name]
        : [selectedDrive.name]
    );
    setValue(`actionData.${idx}.driveRole.${newlyAddedDriveIdx}`, 'reader');
    setValue(
      `actionData.${idx}.driveId.${newlyAddedDriveIdx}`,
      selectedDrive.id
    );
    trigger(`actionData.${idx}.driveName`);
    autocompleteKey.current = new Date().getTime().toString();
    setAutoCompleteResponse('');
    setSelectedDrive(null);
  };

  function removeSelection(driveName: string, index: number) {
    const newList = currentAction.driveName.filter(
      (name) => name !== driveName
    );
    const newDriveRoles = currentAction.driveRole.filter((_, i) => i !== index);
    const newDriveIds = currentAction.driveId.filter((_, i) => i !== index);
    setValue(`actionData.${idx}.driveName`, newList);
    setValue(`actionData.${idx}.driveRole`, newDriveRoles);
    setValue(`actionData.${idx}.driveId`, newDriveIds);
  }

  const driveRoleOptions = [
    { displayValue: 'Viewer', value: 'reader' },
    { displayValue: 'Commenter', value: 'commenter' },
    { displayValue: 'Contributer', value: 'writer' },
    { displayValue: 'Content Manager', value: 'fileOrganizer' },
    { displayValue: 'Manager', value: 'organizer' },
    { displayValue: 'Owner', value: 'owner' },
  ];

  const selectedAllShareDrives = currentAction.driveName?.[0] === '*';

  return (
    <Stack sx={{ p: 1 }}>
      <Stack flexDirection={'row'} gap={1}>
        <Box sx={{ width: 400 }}>
          <Controller
            name={`actionData.${idx}.driveName`}
            control={control}
            render={() =>
              currentTier !== 'enterprise' ? (
                <Tooltip
                  arrow
                  title="This searches for a shared drive whose title contains the word you are typing in. Ex: name contains 'confidential' when typing confidential"
                >
                  <SharedDrivesAutocomplete
                    newKey={`${autocompleteKey.current}`}
                    size="medium"
                    autocompleteValue={autoCompleteResponse}
                    setAutoCompleteValue={setAutoCompleteResponse}
                    disabled={selectedAllShareDrives}
                    onDriveSelect={setSelectedDrive}
                    errors={!!error?.driveName || alreadySelectedError}
                    helperText={
                      alreadySelectedError
                        ? 'Drive is already selected.'
                        : error?.driveName
                        ? error?.driveName?.message
                        : ' '
                    }
                  />
                </Tooltip>
              ) : (
                <SharedDrivesBigQueryAutocomplete
                  newKey={`${autocompleteKey.current}`}
                  autocompleteValue={autoCompleteResponse}
                  setAutoCompleteValue={setAutoCompleteResponse}
                  onDriveSelect={setSelectedDrive}
                  disabled={selectedAllShareDrives}
                  size="medium"
                  errors={!!error?.driveName || alreadySelectedError}
                  helperText={
                    alreadySelectedError
                      ? 'Drive is already selected.'
                      : error?.driveName
                      ? error?.driveName?.message
                      : ' '
                  }
                />
              )
            }
          />
        </Box>
        <Box>
          <Button
            sx={{ height: 40 }}
            variant="contained"
            disabled={autoCompleteResponse.length === 0}
            onClick={() => {
              if (currentAction.driveName.includes(autoCompleteResponse)) {
                setAlreadySelectedError(true);
              } else {
                setAlreadySelectedError(false);
                addSelection();
              }
            }}
          >
            Add
          </Button>
        </Box>
      </Stack>
      <Stack flexDirection={'row'} sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedAllShareDrives}
              onChange={() => {
                if (selectedAllShareDrives) {
                  setValue(`actionData.${idx}.driveName`, []);
                  setValue(`actionData.${idx}.driveRole`, []);
                  setValue(`actionData.${idx}.driveId`, []);
                } else {
                  setValue(`actionData.${idx}.driveName`, ['*']);
                  setValue(`actionData.${idx}.driveId`, ['*']);
                  setValue(`actionData.${idx}.driveRole`, [
                    defaultAllShareDrivesRole,
                  ]);
                  trigger(`actionData.${idx}.driveName`);
                  autocompleteKey.current = new Date().getTime().toString();
                  setAutoCompleteResponse('');
                  setAlreadySelectedError(false);
                }
              }}
            />
          }
          label="Add to all shared drives"
        />
        <TextField
          inputProps={{
            id: 'allShareDrivesRole',
          }}
          InputLabelProps={{
            htmlFor: 'allShareDrivesRole',
          }}
          select
          label="Drive Role"
          size="small"
          autoComplete="off"
          value={defaultAllShareDrivesRole}
          disabled={currentAction?.driveName?.[0] != '*'}
          onChange={(event) => {
            setDefaultAllShareDrivesRole(event.target.value);
            setValue(`actionData.${idx}.driveRole`, [event.target.value]);
          }}
          sx={{ width: 181 }}
        >
          {driveRoleOptions.map((option) => (
            <MenuItem value={option.value} key={option.displayValue}>
              {option.displayValue}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Box sx={{ maxHeight: 200 }}>
        <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 200 }}>
          <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
            <Table
              size={'small'}
              stickyHeader
              aria-label="searched-shareDrives"
            >
              <TableHead>
                <TableRow>
                  <TableCell>Drive Name</TableCell>
                  <TableCell>Drive Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedSharedDrives.map((drive, index) => (
                  <TableRow key={drive.id}>
                    <TableCell>
                      <Stack
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2">{drive.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <TextField
                          inputProps={{
                            id: 'driveRole',
                          }}
                          InputLabelProps={{
                            htmlFor: 'driveRole',
                          }}
                          select
                          label="Drive Role"
                          size="small"
                          autoComplete="off"
                          value={drive.role}
                          onChange={(event) => {
                            const newRoles = [...currentAction.driveRole];
                            newRoles[index] = event.target.value;
                            setValue(`actionData.${idx}.driveRole`, newRoles);
                          }}
                          sx={{ width: 140 }}
                        >
                          {driveRoleOptions.map((option) => (
                            <MenuItem
                              value={option.value}
                              key={option.displayValue}
                            >
                              {option.displayValue}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Tooltip arrow title="Delete">
                          <span>
                            <IconButton
                              onClick={() => removeSelection(drive.name, index)}
                            >
                              <Delete />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Stack>
  );
}
